import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  Trophy,
  Clock,
  DollarSign,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Search,
  Calendar,
  RefreshCw,
  ChevronDown,
  Activity,
  Crown,
  ArrowRight,
  Zap,
  Layers,
  Eye,
  Award,
  Star,
  Percent,
  Hash,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import {
  ErrorState,
  AnalyticsSkeleton,
  LoadingState,
  Tabs,
  TabsList,
  Tab,
  TabContent,
  Tooltip,
  Sparkline,
} from '../../components/ui';
import { ModelLogo, ModelBadge, MODEL_CONFIG, getModelName } from '../../components/ModelLogo';

// Types
interface AnalyticsData {
  summary: {
    totalTestCases: number;
    totalTestRuns: number;
    totalExecutions: number;
    totalEvaluations: number;
    completionRate: number;
    averageAutoScore: number;
  };
  modelPerformance: Array<{
    model: string;
    displayName: string;
    totalRuns: number;
    averageAutoScore: number;
    averageLatencyMs: number;
    averageCostUsd: number;
    verdictBreakdown: Record<string, number>;
    averageRank: number;
    winCount: number;
    winRate: number;
  }>;
  recentTrends: Array<{
    date: string;
    testRuns: number;
    evaluations: number;
    averageScore: number;
  }>;
  categoryBreakdown: Array<{
    category: string;
    testCases: number;
    testRuns: number;
    averageScore: number;
  }>;
}

interface SummaryResponse {
  data: {
    testCases: { total: number; active: number; draft: number };
    testRuns: { total: number; completed: number };
    executions: { total: number; completed: number; successRate: string | number };
    evaluations: { total: number };
    searchExecutions?: { total: number; completed: number; successRate: string | number };
    searchEvaluations?: { total: number };
    searchPhase?: { completed: number; byStatus: Array<{ status: string; count: number }> };
    casesByCategory: Array<{ category: string; count: number }>;
  };
}

interface ModelComparisonResponse {
  data: {
    models: Array<{
      model: string;
      totalExecutions: number;
      successfulExecutions: number;
      averageIndicationLatency: number;
      averageCriteriaLatency: number;
      totalCost: number;
      verdictDistribution: Record<string, number>;
      selectedBestCount: number;
      winRate: string | number;
    }>;
    totalBestSelections: number;
  };
}

interface SearchModelComparisonResponse {
  data: {
    models: Array<{
      model: string;
      totalExecutions: number;
      successfulExecutions: number;
      averageLatencyMs: number;
      averageCostUsd: number;
      averageRelevanceScore: number;
      averageCombinedScore: number;
      verdictDistribution: Record<string, number>;
      selectedBestCount: number;
      winRate: string | number;
    }>;
    modelPairs: Array<{
      searchModel: string;
      criteriaModel: string;
      totalExecutions: number;
      successfulExecutions: number;
      averageAutoScore: number;
      averageLatencyMs: number;
      averageCostUsd: number;
      verdictDistribution: Record<string, number>;
      selectedBestCount: number;
      winRate: string | number;
    }>;
    totalBestSelections: number;
    totalSearchEvaluations: number;
  };
}

interface CategoryPerformanceResponse {
  data: Array<{
    category: string;
    models: Array<{
      model: string;
      executions: number;
      avgScore: number;
      passRate: number;
    }>;
  }>;
}

interface QualityAssessmentResponse {
  data: {
    summary: {
      totalAssessmentSets: number;
      totalProvidersAssessed: number;
      averageTotalScore: number;
      goodMatchCount: number;
      goodMatchRate: number;
    };
    averageDimensionScores: {
      medicalFit: number;
      urgencyTiming: number;
      therapyPathway: number;
      complexityRisk: number;
      outcomeFit: number;
      patientCentricity: number;
    };
    scoreDistribution: {
      excellent: number;
      good: number;
      adequate: number;
      poor: number;
      inadequate: number;
    };
    modelPerformance: Array<{
      model: string;
      totalAssessments: number;
      averageQualityScore: number;
      goodMatchCount: number;
      goodMatchRate: number;
      averageDimensionScores: {
        medicalFit: number;
        urgencyTiming: number;
        therapyPathway: number;
        complexityRisk: number;
        outcomeFit: number;
        patientCentricity: number;
      };
    }>;
    byTestRun: Array<{
      testRunId: string;
      testRunName: string;
      testCaseName: string;
      testCaseCode: string;
      category: string;
      evaluatorName: string;
      assessmentCount: number;
      averageScore: number;
      goodMatchCount: number;
      goodMatchRate: number;
      completedAt: string;
    }>;
    // AI Assessment data
    aiAssessment?: {
      summary: {
        totalAssessments: number;
        totalProvidersAssessed: number;
        averageScore: number;
        goodMatchCount: number;
        goodMatchRate: number;
        disagreementCount: number;
        disagreementRate: number;
      };
      ratingDistribution: {
        excellent: number;
        good: number;
        mixed: number;
        poor: number;
      };
      scoreDistribution: {
        excellent: number;
        good: number;
        adequate: number;
        poor: number;
        inadequate: number;
      };
      averageDimensionScores: {
        medicalFit: number;
        urgencyTiming: number;
        pathwayCoherence: number;
        complexityRisk: number;
        evidenceQuality: number;
        patientCentricity: number;
      };
      modelPerformance: Array<{
        model: string;
        totalAssessments: number;
        averageQualityScore: number;
        goodMatchCount: number;
        goodMatchRate: number;
        averageDimensionScores: {
          medicalFit: number;
          urgencyTiming: number;
          pathwayCoherence: number;
          complexityRisk: number;
          evidenceQuality: number;
          patientCentricity: number;
        };
      }>;
    };
  };
}

interface StabilityAnalyticsResponse {
  data: {
    summary: {
      totalStabilityRuns: number;
      completedRuns: number;
      runsWithPhase2: number;
      phase1HumanEvaluations: number;
      phase2HumanEvaluations: number;
      phase1AIAnalyses: number;
      phase2AIAnalyses: number;
    };
    phase1: {
      human: {
        total: number;
        verdictDistribution: Record<string, number>;
      };
      ai: {
        total: number;
        averageScore: number | null;
        assessmentDistribution: Record<string, number>;
      };
      agreement: {
        comparisons: number;
        agreements: number;
        agreementRate: number | null;
      };
    };
    phase2: {
      human: {
        total: number;
        verdictDistribution: Record<string, number>;
      };
      ai: {
        total: number;
        averageScore: number | null;
        assessmentDistribution: Record<string, number>;
      };
      agreement: {
        comparisons: number;
        agreements: number;
        agreementRate: number | null;
      };
    };
    modelPerformance: Array<{
      model: string;
      totalRuns: number;
      completedRuns: number;
      phase1: {
        humanEvaluations: number;
        verdictDistribution: Record<string, number>;
        aiAnalyses: number;
        averageConsistencyScore: number | null;
      };
      phase2: {
        hasSearchPhase: number;
        humanEvaluations: number;
        verdictDistribution: Record<string, number>;
        aiAnalyses: number;
        averageConsistencyScore: number | null;
      };
    }>;
    byTestRun: Array<{
      testRunId: string;
      testRunName: string;
      model: string;
      testCaseName: string;
      testCaseCode: string;
      category: string;
      status: string;
      totalIterations: number;
      completedIterations: number;
      createdAt: string;
      completedAt: string | null;
      phase1: {
        humanVerdict: string | null;
        humanEvaluator: string | null;
        humanCompletedAt: string | null;
        aiScore: number | null;
        aiAssessment: string | null;
      };
      phase2: {
        hasSearchPhase: boolean;
        searchPhaseStatus: string | null;
        humanVerdict: string | null;
        humanEvaluator: string | null;
        humanCompletedAt: string | null;
        aiScore: number | null;
        aiAssessment: string | null;
      };
    }>;
  };
}

// Time period options
const TIME_PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
] as const;

type TimePeriod = typeof TIME_PERIODS[number]['value'];

// Search verdict colors
const SEARCH_VERDICT_COLORS: Record<string, { bg: string; text: string }> = {
  EXCELLENT: { bg: 'bg-green-500', text: 'text-white' },
  GOOD: { bg: 'bg-blue-500', text: 'text-white' },
  ADEQUATE: { bg: 'bg-amber-500', text: 'text-white' },
  POOR: { bg: 'bg-orange-500', text: 'text-white' },
  INADEQUATE: { bg: 'bg-red-500', text: 'text-white' },
};

// Stat Card Component
function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  trend,
  color = 'amber',
  onClick,
  sparklineData,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  trend?: { value: number; positive?: boolean };
  color?: 'amber' | 'blue' | 'green' | 'purple';
  onClick?: () => void;
  sparklineData?: number[];
}) {
  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  const colorHex = {
    amber: '#f59e0b',
    blue: '#3b82f6',
    green: '#22c55e',
    purple: '#7c3aed',
  };

  return (
    <div
      className={clsx(
        'rounded-xl border border-gray-200 bg-white p-5 transition-all',
        onClick && 'cursor-pointer hover:shadow-md hover:border-amber-300'
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={clsx('rounded-lg p-3', colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
            {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
            {trend && (
              <div className={clsx(
                'flex items-center gap-1 mt-1 text-xs font-medium',
                trend.positive !== false ? 'text-green-600' : 'text-red-600'
              )}>
                {trend.positive !== false ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {trend.positive !== false ? '+' : ''}{trend.value}%
              </div>
            )}
          </div>
        </div>
        {sparklineData && sparklineData.length > 1 && (
          <Sparkline
            data={sparklineData}
            width={60}
            height={32}
            color={colorHex[color]}
          />
        )}
      </div>
    </div>
  );
}

// Model Performance Card V2
function ModelCardV2({
  model,
  isLeader,
  onViewDetails,
  showPercentages = false,
}: {
  model: AnalyticsData['modelPerformance'][0];
  isLeader?: boolean;
  onViewDetails?: () => void;
  showPercentages?: boolean;
}) {
  const config = MODEL_CONFIG[model.model] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
    color: '#6b7280',
  };

  const passCount = (model.verdictBreakdown.PASS || 0) + (model.verdictBreakdown.PASS_WITH_NOTES || 0);
  const failCount = (model.verdictBreakdown.FAIL_MINOR || 0) +
                    (model.verdictBreakdown.FAIL_MAJOR || 0) +
                    (model.verdictBreakdown.FAIL_CRITICAL || 0);
  const marginalCount = model.verdictBreakdown.MARGINAL || 0;
  const totalVerdicts = passCount + failCount + marginalCount;
  const passRate = totalVerdicts > 0 ? (passCount / totalVerdicts) * 100 : 0;

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-amber-400 ring-2 ring-amber-100' : 'border-gray-200',
      )}
    >
      {/* Header */}
      <div className={clsx('px-5 py-4 flex items-center justify-between', config.bgColor)}>
        <div className="flex items-center gap-3">
          <ModelLogo model={model.model} size="md" />
          <div>
            <h3 className={clsx('font-bold', config.textColor)}>{getModelName(model.model)}</h3>
            <p className="text-xs text-gray-500">{model.totalRuns} executions</p>
          </div>
        </div>
        {isLeader && (
          <div className="flex items-center gap-1 rounded-full bg-amber-200 px-2.5 py-1">
            <Crown className="h-3.5 w-3.5 text-amber-700" />
            <span className="text-xs font-semibold text-amber-700">Leader</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="p-5 grid grid-cols-2 gap-4">
        <MetricCell
          label="Win Rate"
          value={`${model.winRate}%`}
          icon={Trophy}
          highlight={model.winRate > 50}
          tooltip="Percentage of evaluations where this model was selected as best"
        />
        <MetricCell
          label="Pass Rate"
          value={`${Math.round(passRate)}%`}
          icon={CheckCircle}
          highlight={passRate >= 80}
          tooltip="Percentage of verdicts that were Pass or Pass with Notes"
        />
        <MetricCell
          label="Avg Latency"
          value={`${(model.averageLatencyMs / 1000).toFixed(1)}s`}
          icon={Clock}
          tooltip="Average time to generate indication and criteria"
        />
        <MetricCell
          label="Avg Cost"
          value={`$${model.averageCostUsd.toFixed(3)}`}
          icon={DollarSign}
          tooltip="Average cost per execution"
        />
      </div>

      {/* Verdict Distribution Bar */}
      {totalVerdicts > 0 && (
        <div className="px-5 pb-4">
          <VerdictBar
            pass={passCount}
            marginal={marginalCount}
            fail={failCount}
            total={totalVerdicts}
            showPercentages={showPercentages}
          />
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-100 px-5 py-3 flex justify-between items-center bg-gray-50">
        <span className="text-xs text-gray-400">{model.winCount} wins total</span>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="inline-flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700"
          >
            View Details
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// Metric Cell Component
function MetricCell({
  label,
  value,
  icon: Icon,
  highlight,
  tooltip,
}: {
  label: string;
  value: string | number;
  icon?: React.ElementType;
  highlight?: boolean;
  tooltip?: string;
}) {
  const content = (
    <div>
      <span className="text-xs text-gray-500 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </span>
      <p className={clsx(
        'text-lg font-bold',
        highlight ? 'text-green-600' : 'text-gray-900'
      )}>
        {value}
      </p>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip}>
        <div className="cursor-help">{content}</div>
      </Tooltip>
    );
  }

  return content;
}

// Verdict Distribution Bar
function VerdictBar({
  pass,
  marginal,
  fail,
  total,
  showPercentages = false,
}: {
  pass: number;
  marginal: number;
  fail: number;
  total: number;
  showPercentages?: boolean;
}) {
  const passPercent = total > 0 ? Math.round((pass / total) * 100) : 0;
  const marginalPercent = total > 0 ? Math.round((marginal / total) * 100) : 0;
  const failPercent = total > 0 ? Math.round((fail / total) * 100) : 0;
  const minBarWidth = showPercentages ? '36px' : '20px';

  return (
    <div>
      <div className="flex items-center gap-0.5 h-5 rounded-full overflow-hidden">
        {pass > 0 && (
          <Tooltip content={`Pass: ${pass} (${passPercent}%)`}>
            <div
              className="h-full bg-green-500 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ width: `${(pass / total) * 100}%`, minWidth: pass > 0 ? minBarWidth : 0 }}
            >
              {showPercentages ? `${passPercent}%` : pass}
            </div>
          </Tooltip>
        )}
        {marginal > 0 && (
          <Tooltip content={`Marginal: ${marginal} (${marginalPercent}%)`}>
            <div
              className="h-full bg-amber-500 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ width: `${(marginal / total) * 100}%`, minWidth: marginal > 0 ? minBarWidth : 0 }}
            >
              {showPercentages ? `${marginalPercent}%` : marginal}
            </div>
          </Tooltip>
        )}
        {fail > 0 && (
          <Tooltip content={`Fail: ${fail} (${failPercent}%)`}>
            <div
              className="h-full bg-red-500 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ width: `${(fail / total) * 100}%`, minWidth: fail > 0 ? minBarWidth : 0 }}
            >
              {showPercentages ? `${failPercent}%` : fail}
            </div>
          </Tooltip>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> Pass
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-500" /> Marginal
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" /> Fail
        </span>
      </div>
    </div>
  );
}

// Search Model Card - aggregated by search model only
function SearchModelCard({
  model,
  isLeader,
  showPercentages = false,
}: {
  model: SearchModelComparisonResponse['data']['models'][0];
  isLeader?: boolean;
  showPercentages?: boolean;
}) {
  const config = MODEL_CONFIG[model.model] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  };

  const totalVerdicts = Object.values(model.verdictDistribution).reduce((a, b) => a + b, 0);

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200',
      )}
    >
      {/* Header */}
      <div className={clsx('px-5 py-4 flex items-center justify-between', config.bgColor)}>
        <div className="flex items-center gap-3">
          <ModelLogo model={model.model} size="md" />
          <div>
            <h3 className={clsx('font-bold', config.textColor)}>{getModelName(model.model)}</h3>
            <p className="text-xs text-gray-500">{model.totalExecutions} searches</p>
          </div>
        </div>
        {isLeader && (
          <div className="flex items-center gap-1 rounded-full bg-blue-200 px-2.5 py-1">
            <Crown className="h-3.5 w-3.5 text-blue-700" />
            <span className="text-xs font-semibold text-blue-700">Leader</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="p-5 grid grid-cols-2 gap-4">
        <MetricCell
          label="Win Rate"
          value={`${model.winRate}%`}
          icon={Trophy}
          highlight={Number(model.winRate) > 50}
          tooltip="Percentage of search evaluations where this model was selected as best"
        />
        <MetricCell
          label="Relevance"
          value={`${Math.round(model.averageRelevanceScore)}%`}
          icon={Target}
          highlight={model.averageRelevanceScore >= 80}
          tooltip="Average relevance score of search results"
        />
        <MetricCell
          label="Avg Latency"
          value={`${(model.averageLatencyMs / 1000).toFixed(1)}s`}
          icon={Clock}
          tooltip="Average time to complete search"
        />
        <MetricCell
          label="Avg Cost"
          value={`$${model.averageCostUsd.toFixed(3)}`}
          icon={DollarSign}
          tooltip="Average cost per search"
        />
      </div>

      {/* Search Verdict Distribution */}
      {totalVerdicts > 0 && (
        <div className="px-5 pb-4">
          <SearchVerdictBar verdicts={model.verdictDistribution} total={totalVerdicts} showPercentages={showPercentages} />
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-100 px-5 py-3 flex justify-between items-center bg-gray-50">
        <span className="text-xs text-gray-400">{model.selectedBestCount} times selected best</span>
      </div>
    </div>
  );
}

// Search Model Pair Card - shows searchModel + criteriaModel combination
type ModelPairData = SearchModelComparisonResponse['data']['modelPairs'][0];

function SearchModelPairCard({
  pair,
  isLeader,
  showPercentages = false,
}: {
  pair: ModelPairData;
  isLeader?: boolean;
  showPercentages?: boolean;
}) {
  const searchConfig = MODEL_CONFIG[pair.searchModel] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  };

  const totalVerdicts = Object.values(pair.verdictDistribution).reduce((a, b) => a + b, 0);

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200',
      )}
    >
      {/* Header - Model Pair */}
      <div className={clsx('px-5 py-4', searchConfig.bgColor)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ModelLogo model={pair.searchModel} size="md" />
            <div>
              <h3 className={clsx('font-bold text-sm', searchConfig.textColor)}>{getModelName(pair.searchModel)}</h3>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Search</p>
            </div>
          </div>
          {isLeader && (
            <div className="flex items-center gap-1 rounded-full bg-blue-200 px-2.5 py-1">
              <Crown className="h-3.5 w-3.5 text-blue-700" />
              <span className="text-xs font-semibold text-blue-700">Leader</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 uppercase">criteria from</span>
          <ModelBadge model={pair.criteriaModel} size="sm" />
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-5 grid grid-cols-2 gap-4">
        <MetricCell
          label="Win Rate"
          value={`${pair.winRate}%`}
          icon={Trophy}
          highlight={Number(pair.winRate) > 50}
          tooltip="Percentage of evaluations where this pair was selected as best"
        />
        <MetricCell
          label="Auto Score"
          value={`${Math.round(pair.averageAutoScore)}%`}
          icon={Target}
          highlight={pair.averageAutoScore >= 80}
          tooltip="Average combined auto-score of search results"
        />
        <MetricCell
          label="Avg Latency"
          value={`${(pair.averageLatencyMs / 1000).toFixed(1)}s`}
          icon={Clock}
          tooltip="Average time to complete search"
        />
        <MetricCell
          label="Avg Cost"
          value={`$${pair.averageCostUsd.toFixed(3)}`}
          icon={DollarSign}
          tooltip="Average cost per search"
        />
      </div>

      {/* Search Verdict Distribution */}
      {totalVerdicts > 0 && (
        <div className="px-5 pb-4">
          <SearchVerdictBar verdicts={pair.verdictDistribution} total={totalVerdicts} showPercentages={showPercentages} />
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-100 px-5 py-3 flex justify-between items-center bg-gray-50">
        <span className="text-xs text-gray-400">{pair.selectedBestCount} times selected best</span>
        <span className="text-xs text-gray-400">{pair.totalExecutions} searches</span>
      </div>
    </div>
  );
}

// Search Verdict Bar
function SearchVerdictBar({
  verdicts,
  total,
  showPercentages = false,
}: {
  verdicts: Record<string, number>;
  total: number;
  showPercentages?: boolean;
}) {
  const verdictOrder = ['EXCELLENT', 'GOOD', 'ADEQUATE', 'POOR', 'INADEQUATE'];
  const minBarWidth = showPercentages ? '36px' : '20px';

  return (
    <div>
      <div className="flex items-center gap-0.5 h-5 rounded-full overflow-hidden">
        {verdictOrder.map((verdict) => {
          const count = verdicts[verdict] || 0;
          if (count === 0) return null;
          const colors = SEARCH_VERDICT_COLORS[verdict];
          const percent = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <Tooltip key={verdict} content={`${verdict}: ${count} (${percent}%)`}>
              <div
                className={clsx('h-full flex items-center justify-center text-[10px] font-medium', colors.bg, colors.text)}
                style={{ width: `${(count / total) * 100}%`, minWidth: minBarWidth }}
              >
                {showPercentages ? `${percent}%` : count}
              </div>
            </Tooltip>
          );
        })}
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> Excellent
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500" /> Good
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-500" /> Adequate
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" /> Poor
        </span>
      </div>
    </div>
  );
}

// Percentage Toggle Button - Segmented control for Count vs Percent display
function PercentageToggle({
  showPercentages,
  onToggle,
}: {
  showPercentages: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      <button
        onClick={() => !showPercentages || onToggle()}
        className={clsx(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
          !showPercentages
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        )}
      >
        <Hash className="h-3.5 w-3.5" />
        Count
      </button>
      <button
        onClick={() => showPercentages || onToggle()}
        className={clsx(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
          showPercentages
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        )}
      >
        <Percent className="h-3.5 w-3.5" />
        Percent
      </button>
    </div>
  );
}

// Category Performance Table
function CategoryTable({
  data,
  onRowClick,
}: {
  data: AnalyticsData['categoryBreakdown'];
  onRowClick?: (category: string) => void;
}) {
  const [sortField, setSortField] = useState<'category' | 'testCases' | 'testRuns' | 'averageScore'>('averageScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortableHeader
              label="Category"
              field="category"
              currentField={sortField}
              direction={sortDir}
              onSort={() => handleSort('category')}
            />
            <SortableHeader
              label="Test Cases"
              field="testCases"
              currentField={sortField}
              direction={sortDir}
              onSort={() => handleSort('testCases')}
              align="right"
            />
            <SortableHeader
              label="Executions"
              field="testRuns"
              currentField={sortField}
              direction={sortDir}
              onSort={() => handleSort('testRuns')}
              align="right"
            />
            <SortableHeader
              label="Avg Score"
              field="averageScore"
              currentField={sortField}
              direction={sortDir}
              onSort={() => handleSort('averageScore')}
              align="right"
            />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sortedData.map((cat) => (
            <tr
              key={cat.category}
              className={clsx(
                'hover:bg-gray-50 transition-colors',
                onRowClick && 'cursor-pointer'
              )}
              onClick={() => onRowClick?.(cat.category)}
            >
              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                {cat.category.replace(/_/g, ' ')}
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-600">
                {cat.testCases}
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-600">
                {cat.testRuns}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-16 h-2 bg-gray-200 rounded-full">
                    <div
                      className={clsx(
                        'h-2 rounded-full transition-all',
                        cat.averageScore >= 80 ? 'bg-green-500' :
                        cat.averageScore >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      )}
                      style={{ width: `${cat.averageScore}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-10">
                    {Math.round(cat.averageScore)}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Sortable Header
function SortableHeader({
  label,
  field,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  currentField: string;
  direction: 'asc' | 'desc';
  onSort: () => void;
  align?: 'left' | 'right';
}) {
  const isActive = currentField === field;

  return (
    <th
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:bg-gray-50 transition-colors',
        align === 'right' ? 'text-right' : 'text-left',
        isActive ? 'text-amber-600' : 'text-gray-500'
      )}
      onClick={onSort}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-amber-500">{direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );
}

// Latency/Cost Comparison Chart
function ComparisonChart({
  data,
  type,
}: {
  data: Array<{ model: string; value: number }>;
  type: 'latency' | 'cost';
}) {
  // Filter out models with no data (value = 0) for determining the best
  const validData = data.filter(d => d.value > 0);
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const sortedData = [...data].sort((a, b) => a.value - b.value);
  // Only consider models with actual data for "Fastest/Cheapest" label
  const sortedValidData = [...validData].sort((a, b) => a.value - b.value);
  const fastest = sortedValidData[0]?.model;

  return (
    <div className="space-y-3">
      {sortedData.map((item) => {
        const config = MODEL_CONFIG[item.model] || { bgColor: 'bg-gray-100', textColor: 'text-gray-700', color: '#6b7280' };
        const percentage = (item.value / maxValue) * 100;
        const isBest = item.model === fastest;
        const formattedValue = type === 'latency'
          ? `${(item.value / 1000).toFixed(1)}s`
          : `$${item.value.toFixed(4)}`;

        return (
          <div key={item.model}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-2">
                <ModelLogo model={item.model} size="xs" />
                <span className={clsx('font-medium', config.textColor)}>
                  {getModelName(item.model)}
                </span>
                {isBest && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                    {type === 'latency' ? 'Fastest' : 'Cheapest'}
                  </span>
                )}
              </span>
              <span className="text-gray-600 font-medium">{formattedValue}</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: config.color,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Overview Quick Stats
function OverviewQuickStats({
  phase1Data,
  phase2Data,
  phase2PairData,
  phase3Data,
  summaryData,
}: {
  phase1Data: AnalyticsData['modelPerformance'];
  phase2Data?: SearchModelComparisonResponse['data']['models'];
  phase2PairData?: SearchModelComparisonResponse['data']['modelPairs'];
  phase3Data?: QualityAssessmentResponse['data'];
  summaryData: SummaryResponse['data'];
}) {
  // Find leaders
  const phase1Leader = phase1Data.reduce((best, current) =>
    current.winCount > (best?.winCount || 0) ? current : best
  , phase1Data[0]);

  const phase2Leader = phase2Data?.reduce((best, current) =>
    current.selectedBestCount > (best?.selectedBestCount || 0) ? current : best
  , phase2Data[0]);

  // Find best model pair (by win count)
  const activePairs = (phase2PairData || []).filter(p => p.totalExecutions > 0);
  const phase2LeaderPair = activePairs.length > 0
    ? activePairs.reduce((best, current) =>
        current.selectedBestCount > (best?.selectedBestCount || 0) ? current : best
      , activePairs[0])
    : null;

  // Phase 5 leader is the first model (already sorted by goodMatchRate, then averageQualityScore)
  const phase3Leader = phase3Data?.modelPerformance?.[0];
  const hasPhase3Data = (phase3Data?.summary.totalAssessmentSets ?? 0) > 0;

  return (
    <div className={`grid gap-4 ${hasPhase3Data ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
      {/* Total Activity */}
      <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-amber-50 to-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-lg bg-amber-100 p-2.5">
            <Activity className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Total Activity</h3>
            <p className="text-xs text-gray-500">All-time validation metrics</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold text-gray-900">{summaryData.testCases.total}</p>
            <p className="text-xs text-gray-500">Test Cases</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summaryData.testRuns.total}</p>
            <p className="text-xs text-gray-500">Test Runs</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summaryData.executions.total}</p>
            <p className="text-xs text-gray-500">Executions</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summaryData.evaluations.total}</p>
            <p className="text-xs text-gray-500">Evaluations</p>
          </div>
        </div>
      </div>

      {/* Phase 2+3 Leader */}
      {phase1Leader && (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-lg bg-purple-100 p-2.5">
              <Layers className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Phase 2+3 Leader</h3>
              <p className="text-xs text-gray-500">Indication & Matching Criteria</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModelLogo model={phase1Leader.model} size="lg" />
            <div>
              <p className="font-bold text-gray-900">{getModelName(phase1Leader.model)}</p>
              <div className="flex items-center gap-2 mt-1">
                <Trophy className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-gray-600">{phase1Leader.winCount} wins</span>
                <span className="text-xs text-gray-400">({phase1Leader.winRate}% win rate)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 4 Leader */}
      {phase2LeaderPair ? (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-lg bg-blue-100 p-2.5">
              <Search className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Phase 4 Leader</h3>
              <p className="text-xs text-gray-500">Care Provider Matching</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModelLogo model={phase2LeaderPair.searchModel} size="lg" />
            <div>
              <p className="font-bold text-gray-900">{getModelName(phase2LeaderPair.searchModel)}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] text-gray-400">criteria from</span>
                <ModelBadge model={phase2LeaderPair.criteriaModel} size="sm" />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Trophy className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-600">{phase2LeaderPair.selectedBestCount} wins</span>
                <span className="text-xs text-gray-400">({phase2LeaderPair.winRate}% win rate)</span>
              </div>
            </div>
          </div>
        </div>
      ) : phase2Leader ? (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50 to-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-lg bg-blue-100 p-2.5">
              <Search className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Phase 4 Leader</h3>
              <p className="text-xs text-gray-500">Care Provider Matching</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModelLogo model={phase2Leader.model} size="lg" />
            <div>
              <p className="font-bold text-gray-900">{getModelName(phase2Leader.model)}</p>
              <div className="flex items-center gap-2 mt-1">
                <Trophy className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-600">{phase2Leader.selectedBestCount} wins</span>
                <span className="text-xs text-gray-400">({phase2Leader.winRate}% win rate)</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Phase 5 Leader */}
      {phase3Leader && (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-lg bg-green-100 p-2.5">
              <Award className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Phase 5 Leader</h3>
              <p className="text-xs text-gray-500">Human Quality Assessment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModelLogo model={phase3Leader.model} size="lg" />
            <div>
              <p className="font-bold text-gray-900">{getModelName(phase3Leader.model)}</p>
              <div className="flex items-center gap-2 mt-1">
                <Trophy className="h-4 w-4 text-green-500" />
                <span className="text-sm text-gray-600">{phase3Leader.goodMatchRate.toFixed(1)}% good match</span>
                <span className="text-xs text-gray-400">({phase3Leader.averageQualityScore.toFixed(1)}/16 avg)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
export function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [isLongLoading, setIsLongLoading] = useState(false);
  const [showPercentages, setShowPercentages] = useState(false);

  // Sync tab with URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Fetch summary data
  const { data: summaryData, isLoading: loadingSummary, error: summaryError, isFetching: fetchingSummary } = useQuery({
    queryKey: ['validation', 'analytics', 'summary', timePeriod],
    queryFn: () => api.get<SummaryResponse>(`/api/validation/analytics/summary${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  // Fetch model comparison data (Phase 2+3)
  const { data: comparisonData, isLoading: loadingComparison, isFetching: fetchingComparison } = useQuery({
    queryKey: ['validation', 'analytics', 'model-comparison', timePeriod],
    queryFn: () => api.get<ModelComparisonResponse>(`/api/validation/analytics/model-comparison${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  // Fetch search model comparison data (Phase 4)
  const { data: searchComparisonData, isLoading: loadingSearchComparison, isFetching: fetchingSearchComparison } = useQuery({
    queryKey: ['validation', 'analytics', 'search-model-comparison', timePeriod],
    queryFn: () => api.get<SearchModelComparisonResponse>(`/api/validation/analytics/search-model-comparison${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  // Fetch category performance data
  const { data: categoryData, isLoading: loadingCategory, isFetching: fetchingCategory } = useQuery({
    queryKey: ['validation', 'analytics', 'by-category', timePeriod],
    queryFn: () => api.get<CategoryPerformanceResponse>(`/api/validation/analytics/by-category${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  // Fetch quality assessment analytics data (Phase 5)
  const { data: qualityAssessmentData, isLoading: loadingQualityAssessment, isFetching: fetchingQualityAssessment } = useQuery({
    queryKey: ['validation', 'analytics', 'quality-assessment', timePeriod],
    queryFn: () => api.get<QualityAssessmentResponse>(`/api/validation/analytics/quality-assessment${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  // Fetch stability analytics data
  const { data: stabilityData, isLoading: loadingStability, isFetching: fetchingStability } = useQuery({
    queryKey: ['validation', 'analytics', 'stability', timePeriod],
    queryFn: () => api.get<StabilityAnalyticsResponse>(`/api/validation/analytics/stability${timePeriod !== 'all' ? `?period=${timePeriod}` : ''}`),
    staleTime: 30000,
  });

  const isLoading = loadingSummary || loadingComparison || loadingSearchComparison || loadingCategory || loadingQualityAssessment || loadingStability;
  const isFetching = fetchingSummary || fetchingComparison || fetchingSearchComparison || fetchingCategory || fetchingQualityAssessment || fetchingStability;
  const error = summaryError;

  // Long loading detection
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setIsLongLoading(true), 5000);
      return () => clearTimeout(timer);
    }
    setIsLongLoading(false);
  }, [isLoading]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['validation', 'analytics'] });
  };

  const handleCancelLoad = () => {
    queryClient.cancelQueries({ queryKey: ['validation', 'analytics'] });
  };

  // Transform data
  const data: AnalyticsData | null = summaryData ? {
    summary: {
      totalTestCases: summaryData.data.testCases.total,
      totalTestRuns: summaryData.data.testRuns.total,
      totalExecutions: summaryData.data.executions.total,
      totalEvaluations: summaryData.data.evaluations.total,
      completionRate: summaryData.data.testRuns.total > 0
        ? (summaryData.data.testRuns.completed / summaryData.data.testRuns.total) * 100
        : 0,
      averageAutoScore: 0,
    },
    modelPerformance: comparisonData?.data.models.map((m) => ({
      model: m.model,
      displayName: getModelName(m.model),
      totalRuns: m.totalExecutions,
      averageAutoScore: 0,
      averageLatencyMs: m.averageIndicationLatency + m.averageCriteriaLatency,
      averageCostUsd: m.totalCost / Math.max(m.totalExecutions, 1),
      verdictBreakdown: m.verdictDistribution,
      averageRank: 0,
      winCount: m.selectedBestCount,
      winRate: typeof m.winRate === 'string' ? parseFloat(m.winRate) : m.winRate,
    })) || [],
    recentTrends: [],
    categoryBreakdown: summaryData.data.casesByCategory.map((c) => {
      const categoryPerf = categoryData?.data.find((cat) => cat.category === c.category);
      const totalRuns = categoryPerf?.models.reduce((sum, m) => sum + m.executions, 0) || 0;
      const avgScore = categoryPerf?.models.length
        ? categoryPerf.models.reduce((sum, m) => sum + m.avgScore, 0) / categoryPerf.models.length
        : 0;
      return {
        category: c.category,
        testCases: c.count,
        testRuns: totalRuns,
        averageScore: avgScore,
      };
    }),
  } : null;

  // Find leaders
  const phase1Leader = data?.modelPerformance.reduce((best, current) =>
    current.winCount > (best?.winCount || 0) ? current : best
  , data.modelPerformance[0])?.model;

  const phase2Leader = searchComparisonData?.data.models.reduce((best, current) =>
    current.selectedBestCount > (best?.selectedBestCount || 0) ? current : best
  , searchComparisonData.data.models[0])?.model;

  // Loading state
  if (isLoading) {
    if (isLongLoading) {
      return (
        <div className="space-y-6">
          <LoadingState
            message="Loading analytics data..."
            showLongLoadingMessage={true}
            longLoadingThreshold={0}
            longLoadingMessage="This is taking longer than expected. The server might be processing a large dataset."
            onCancel={handleCancelLoad}
          />
        </div>
      );
    }
    return <AnalyticsSkeleton />;
  }

  // Error state
  if (error || !data) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm text-gray-500">Model performance insights and validation metrics</p>
          </div>
        </div>
        <ErrorState
          title="Failed to Load Analytics"
          message={error instanceof Error ? error.message : 'There was an error loading the analytics data.'}
          helpText="Try refreshing the page or check if the API is running"
          onRetry={handleRefresh}
          variant="card"
        />
      </div>
    );
  }

  const hasPhase2Data = (summaryData?.data.searchExecutions?.total ?? 0) > 0;
  const hasPhase3Data = (qualityAssessmentData?.data.summary.totalAssessmentSets ?? 0) > 0;
  const hasAIAssessmentData = (qualityAssessmentData?.data.aiAssessment?.summary.totalProvidersAssessed ?? 0) > 0;
  const hasStabilityData = (stabilityData?.data.summary.totalStabilityRuns ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-sm text-gray-500">Model performance insights and validation metrics</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Period Selector */}
          <div className="relative">
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
              className={clsx(
                'appearance-none rounded-lg border py-2 pl-9 pr-8 text-sm font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1',
                timePeriod !== 'all'
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
            >
              {TIME_PERIODS.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
            <Calendar className={clsx(
              'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
              timePeriod !== 'all' ? 'text-amber-500' : 'text-gray-400'
            )} />
            <ChevronDown className={clsx(
              'pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2',
              timePeriod !== 'all' ? 'text-amber-500' : 'text-gray-400'
            )} />
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
              isFetching
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            )}
          >
            <RefreshCw className={clsx('h-4 w-4', isFetching && 'animate-spin')} />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Fetching Indicator */}
      {isFetching && !isLoading && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Updating data...
        </div>
      )}

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onChange={handleTabChange}>
        <TabsList className="mb-6">
          <Tab value="overview" icon={Activity}>Overview</Tab>
          <Tab value="phase1" icon={Layers}>Phase 2+3: Case Analysis</Tab>
          {hasPhase2Data && (
            <Tab value="phase2" icon={Search}>Phase 4: Care Provider Matching</Tab>
          )}
          {hasPhase3Data && (
            <Tab value="phase3" icon={Award}>Phase 5: Human Quality</Tab>
          )}
          {hasAIAssessmentData && (
            <Tab value="phase3-ai" icon={Zap}>Phase 5: AI Quality</Tab>
          )}
          {hasStabilityData && (
            <Tab value="stability" icon={TrendingUp}>Stability Tests</Tab>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabContent value="overview">
          <div className="space-y-6">
            {/* Quick Stats */}
            {summaryData?.data && (
              <OverviewQuickStats
                phase1Data={data.modelPerformance}
                phase2Data={searchComparisonData?.data.models}
                phase2PairData={searchComparisonData?.data.modelPairs}
                phase3Data={qualityAssessmentData?.data}
                summaryData={summaryData.data}
              />
            )}

            {/* Combined Summary Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={Target}
                label="Test Cases"
                value={data.summary.totalTestCases}
                subValue={`${summaryData?.data.testCases.active ?? 0} active`}
                color="amber"
                onClick={() => navigate('/validation/test-cases')}
              />
              <StatCard
                icon={BarChart3}
                label="Test Runs"
                value={data.summary.totalTestRuns}
                subValue={`${summaryData?.data.testRuns.completed ?? 0} completed`}
                color="blue"
                onClick={() => navigate('/validation/test-runs')}
              />
              <StatCard
                icon={Eye}
                label="Evaluations"
                value={data.summary.totalEvaluations}
                color="green"
                onClick={() => navigate('/validation/evaluations')}
              />
              <StatCard
                icon={CheckCircle}
                label="Completion Rate"
                value={`${Math.round(data.summary.completionRate)}%`}
                color="purple"
              />
            </div>

            {/* Category Breakdown */}
            {data.categoryBreakdown.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Target className="h-5 w-5 text-gray-400" />
                  Performance by Category
                </h2>
                <CategoryTable
                  data={data.categoryBreakdown}
                  onRowClick={(category) => navigate(`/validation/test-cases?category=${category}`)}
                />
              </div>
            )}

            {/* Stability Summary */}
            {hasStabilityData && stabilityData && (
              <div className="rounded-xl border border-purple-200 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    Stability Testing Summary
                  </h2>
                  <button
                    onClick={() => handleTabChange('stability')}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    View details
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Stability Test Runs</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {stabilityData.data.summary.totalStabilityRuns}
                    </div>
                    <div className="text-xs text-gray-500">
                      {stabilityData.data.summary.completedRuns} completed
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Phase 1 Evaluations</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {stabilityData.data.summary.phase1HumanEvaluations}
                    </div>
                    <div className="text-xs text-gray-500">
                      {stabilityData.data.summary.phase1AIAnalyses} AI analyses
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Phase 2 Evaluations</div>
                    <div className="text-2xl font-bold text-green-600">
                      {stabilityData.data.summary.phase2HumanEvaluations}
                    </div>
                    <div className="text-xs text-gray-500">
                      {stabilityData.data.summary.phase2AIAnalyses} AI analyses
                    </div>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Human-AI Agreement</div>
                    <div className="text-2xl font-bold text-amber-600">
                      {stabilityData.data.phase1.agreement.agreementRate ?? 'N/A'}%
                    </div>
                    <div className="text-xs text-gray-500">
                      Phase 1 ({stabilityData.data.phase1.agreement.comparisons} comparisons)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabContent>

        {/* Phase 2+3 Tab */}
        <TabContent value="phase1">
          <div className="space-y-6">
            {/* Phase 2+3 Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={Target}
                label="Test Cases"
                value={data.summary.totalTestCases}
                color="amber"
                onClick={() => navigate('/validation/test-cases')}
              />
              <StatCard
                icon={BarChart3}
                label="Test Runs"
                value={data.summary.totalTestRuns}
                color="blue"
                onClick={() => navigate('/validation/test-runs')}
              />
              <StatCard
                icon={Eye}
                label="Evaluations"
                value={data.summary.totalEvaluations}
                color="green"
                onClick={() => navigate('/validation/evaluations')}
              />
              <StatCard
                icon={Zap}
                label="Success Rate"
                value={`${summaryData?.data.executions.successRate ?? 0}%`}
                color="purple"
              />
            </div>

            {/* Model Performance Cards */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Model Performance
                </h2>
                <PercentageToggle
                  showPercentages={showPercentages}
                  onToggle={() => setShowPercentages(!showPercentages)}
                />
              </div>
              {data.modelPerformance.length > 0 ? (
                <div className="grid gap-4 lg:grid-cols-3">
                  {data.modelPerformance.map((model) => (
                    <ModelCardV2
                      key={model.model}
                      model={model}
                      isLeader={model.model === phase1Leader}
                      onViewDetails={() => navigate(`/validation/evaluations?model=${model.model}`)}
                      showPercentages={showPercentages}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
                  <BarChart3 className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">
                    No model performance data yet. Run some test cases to see results.
                  </p>
                </div>
              )}
            </div>

            {/* Latency & Cost Comparison */}
            {data.modelPerformance.length > 0 && (
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Clock className="h-5 w-5 text-gray-400" />
                    Latency Comparison
                  </h2>
                  <ComparisonChart
                    data={data.modelPerformance.map(m => ({
                      model: m.model,
                      value: m.averageLatencyMs,
                    }))}
                    type="latency"
                  />
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                    Cost Comparison
                  </h2>
                  <ComparisonChart
                    data={data.modelPerformance.map(m => ({
                      model: m.model,
                      value: m.averageCostUsd,
                    }))}
                    type="cost"
                  />
                </div>
              </div>
            )}
          </div>
        </TabContent>

        {/* Phase 4 Tab */}
        {hasPhase2Data && (
          <TabContent value="phase2">
            <div className="space-y-6">
              {/* Phase 4 Stats */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={Search}
                  label="Matching Executions"
                  value={summaryData?.data.searchExecutions?.total || 0}
                  subValue={`${summaryData?.data.searchExecutions?.completed || 0} completed`}
                  color="blue"
                  onClick={() => navigate('/validation/test-runs')}
                />
                <StatCard
                  icon={Eye}
                  label="Matching Evaluations"
                  value={summaryData?.data.searchEvaluations?.total || 0}
                  color="green"
                  onClick={() => navigate('/validation/evaluations')}
                />
                <StatCard
                  icon={TrendingUp}
                  label="Success Rate"
                  value={`${summaryData?.data.searchExecutions?.successRate || 0}%`}
                  color="amber"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Phase Complete"
                  value={summaryData?.data.searchPhase?.completed || 0}
                  subValue="test runs"
                  color="purple"
                  onClick={() => navigate('/validation/test-runs')}
                />
              </div>

              {/* Search Model Performance (aggregated by search model) */}
              {searchComparisonData?.data.models && searchComparisonData.data.models.length > 0 && (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <Search className="h-5 w-5 text-blue-500" />
                      Search Model Performance
                    </h2>
                    <PercentageToggle
                      showPercentages={showPercentages}
                      onToggle={() => setShowPercentages(!showPercentages)}
                    />
                  </div>
                  <div className="grid gap-4 lg:grid-cols-3">
                    {searchComparisonData.data.models.map((model) => (
                      <SearchModelCard
                        key={model.model}
                        model={model}
                        isLeader={model.model === phase2Leader}
                        showPercentages={showPercentages}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Search Latency & Cost Comparison (by search model) */}
              {searchComparisonData?.data.models && searchComparisonData.data.models.length > 0 && (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-white p-6">
                    <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <Clock className="h-5 w-5 text-blue-400" />
                      Search Latency Comparison
                    </h2>
                    <ComparisonChart
                      data={searchComparisonData.data.models.map(m => ({
                        model: m.model,
                        value: m.averageLatencyMs,
                      }))}
                      type="latency"
                    />
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-6">
                    <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <DollarSign className="h-5 w-5 text-blue-400" />
                      Search Cost Comparison
                    </h2>
                    <ComparisonChart
                      data={searchComparisonData.data.models.map(m => ({
                        model: m.model,
                        value: m.averageCostUsd,
                      }))}
                      type="cost"
                    />
                  </div>
                </div>
              )}

              {/* Search Model Pair Performance Cards */}
              {(() => {
                const activePairs = (searchComparisonData?.data.modelPairs || [])
                  .filter(p => p.totalExecutions > 0)
                  .sort((a, b) => Number(b.winRate) - Number(a.winRate));
                const leaderPair = activePairs[0];

                return activePairs.length > 0 ? (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <Layers className="h-5 w-5 text-blue-500" />
                        Model Pair Performance
                      </h2>
                      <PercentageToggle
                        showPercentages={showPercentages}
                        onToggle={() => setShowPercentages(!showPercentages)}
                      />
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                      {activePairs.map((pair) => (
                        <SearchModelPairCard
                          key={`${pair.searchModel}::${pair.criteriaModel}`}
                          pair={pair}
                          isLeader={leaderPair && pair.searchModel === leaderPair.searchModel && pair.criteriaModel === leaderPair.criteriaModel}
                          showPercentages={showPercentages}
                        />
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Search Latency & Cost Comparison (by model pair) */}
              {(() => {
                const activePairs = (searchComparisonData?.data.modelPairs || [])
                  .filter(p => p.totalExecutions > 0)
                  .sort((a, b) => Number(b.winRate) - Number(a.winRate));

                return activePairs.length > 0 ? (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-6">
                      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <Clock className="h-5 w-5 text-blue-400" />
                        Search Latency Comparison
                      </h2>
                      <ComparisonChart
                        data={activePairs.map(p => ({
                          model: `${getModelName(p.searchModel)} + ${getModelName(p.criteriaModel)}`,
                          value: p.averageLatencyMs,
                        }))}
                        type="latency"
                      />
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-6">
                      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                        <DollarSign className="h-5 w-5 text-blue-400" />
                        Search Cost Comparison
                      </h2>
                      <ComparisonChart
                        data={activePairs.map(p => ({
                          model: `${getModelName(p.searchModel)} + ${getModelName(p.criteriaModel)}`,
                          value: p.averageCostUsd,
                        }))}
                        type="cost"
                      />
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </TabContent>
        )}

        {/* Phase 5 Tab - Human Quality Assessment */}
        {hasPhase3Data && qualityAssessmentData && (
          <TabContent value="phase3">
            <div className="space-y-6">
              {/* Phase 5 Stats */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={Award}
                  label="Assessment Sets"
                  value={qualityAssessmentData.data.summary.totalAssessmentSets}
                  subValue="completed"
                  color="green"
                  onClick={() => navigate('/validation/test-runs')}
                />
                <StatCard
                  icon={Eye}
                  label="Providers Assessed"
                  value={qualityAssessmentData.data.summary.totalProvidersAssessed}
                  color="blue"
                />
                <StatCard
                  icon={Star}
                  label="Average Score"
                  value={`${qualityAssessmentData.data.summary.averageTotalScore}/16`}
                  subValue={`${((qualityAssessmentData.data.summary.averageTotalScore / 16) * 100).toFixed(0)}% of max`}
                  color="amber"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Good Match Rate"
                  value={`${qualityAssessmentData.data.summary.goodMatchRate}%`}
                  subValue={`${qualityAssessmentData.data.summary.goodMatchCount} providers`}
                  color="purple"
                />
              </div>

              {/* Model Performance Cards */}
              {qualityAssessmentData.data.modelPerformance.length > 0 && (
                <div>
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Trophy className="h-5 w-5 text-green-500" />
                    Human Assessment - Model Performance
                  </h2>
                  <div className="grid gap-4 lg:grid-cols-3">
                    {qualityAssessmentData.data.modelPerformance.map((model, index) => (
                      <QualityModelCard
                        key={model.model}
                        model={model}
                        isLeader={index === 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Score Distribution */}
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <BarChart3 className="h-5 w-5 text-green-400" />
                    Score Distribution
                  </h2>
                  <PercentageToggle
                    showPercentages={showPercentages}
                    onToggle={() => setShowPercentages(!showPercentages)}
                  />
                </div>
                <div className="space-y-4">
                  {(() => {
                    const total = qualityAssessmentData.data.summary.totalProvidersAssessed;
                    const dist = qualityAssessmentData.data.scoreDistribution;
                    const minBarWidth = showPercentages ? '48px' : '32px';
                    const getDisplay = (count: number) => {
                      if (count === 0) return '';
                      return showPercentages ? `${Math.round((count / total) * 100)}%` : count;
                    };
                    return (
                      <>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Excellent</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-green-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.excellent / total) * 100 : 0}%`,
                                minWidth: dist.excellent > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.excellent)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">13-16</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Good</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.good / total) * 100 : 0}%`,
                                minWidth: dist.good > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.good)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">10-12</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Adequate</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-amber-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.adequate / total) * 100 : 0}%`,
                                minWidth: dist.adequate > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.adequate)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">7-9</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Poor</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-orange-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.poor / total) * 100 : 0}%`,
                                minWidth: dist.poor > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.poor)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">4-6</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Inadequate</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-red-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.inadequate / total) * 100 : 0}%`,
                                minWidth: dist.inadequate > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.inadequate)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">0-3</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Average Dimension Scores */}
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Target className="h-5 w-5 text-green-400" />
                  Human Assessment - Dimension Scores
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <DimensionScoreCard
                    label="Medical Fit"
                    score={qualityAssessmentData.data.averageDimensionScores.medicalFit}
                    max={3}
                    description="Condition-specialty alignment"
                  />
                  <DimensionScoreCard
                    label="Urgency/Timing"
                    score={qualityAssessmentData.data.averageDimensionScores.urgencyTiming}
                    max={3}
                    description="Timing and availability fit"
                  />
                  <DimensionScoreCard
                    label="Therapy Pathway"
                    score={qualityAssessmentData.data.averageDimensionScores.therapyPathway}
                    max={3}
                    description="Treatment approach coherence"
                  />
                  <DimensionScoreCard
                    label="Complexity/Risk"
                    score={qualityAssessmentData.data.averageDimensionScores.complexityRisk}
                    max={2}
                    description="Case complexity handling"
                  />
                  <DimensionScoreCard
                    label="Outcome Fit"
                    score={qualityAssessmentData.data.averageDimensionScores.outcomeFit}
                    max={3}
                    description="Expected outcome alignment"
                  />
                  <DimensionScoreCard
                    label="Patient Centricity"
                    score={qualityAssessmentData.data.averageDimensionScores.patientCentricity}
                    max={2}
                    description="Patient preferences fit"
                  />
                </div>
              </div>

              {/* Assessments by Test Run */}
              {qualityAssessmentData.data.byTestRun.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <BarChart3 className="h-5 w-5 text-gray-400" />
                    Assessments by Test Run
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Test Run
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Test Case
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Evaluator
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Providers
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Avg Score
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Good Match Rate
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Completed
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {qualityAssessmentData.data.byTestRun.map((testRun) => (
                          <tr
                            key={testRun.testRunId}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/validation/test-runs/${testRun.testRunId}`)}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {testRun.testRunName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div>{testRun.testCaseName}</div>
                              <div className="text-xs text-gray-400">{testRun.testCaseCode}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{testRun.evaluatorName}</td>
                            <td className="px-4 py-3 text-right text-sm text-gray-600">
                              {testRun.assessmentCount}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-gray-900">
                                {testRun.averageScore.toFixed(1)}/16
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span
                                className={clsx(
                                  'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                                  testRun.goodMatchRate >= 80
                                    ? 'bg-green-100 text-green-700'
                                    : testRun.goodMatchRate >= 60
                                    ? 'bg-blue-100 text-blue-700'
                                    : testRun.goodMatchRate >= 40
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                                )}
                              >
                                {testRun.goodMatchRate.toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-500">
                              {new Date(testRun.completedAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabContent>
        )}

        {/* Phase 5 Tab - AI Quality Assessment */}
        {hasAIAssessmentData && qualityAssessmentData?.data.aiAssessment && (
          <TabContent value="phase3-ai">
            <div className="space-y-6">
              {/* AI Quality Stats */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={Zap}
                  label="AI Assessment Sets"
                  value={qualityAssessmentData.data.aiAssessment.summary.totalAssessments}
                  subValue="completed"
                  color="purple"
                />
                <StatCard
                  icon={Eye}
                  label="Providers Assessed"
                  value={qualityAssessmentData.data.aiAssessment.summary.totalProvidersAssessed}
                  color="blue"
                />
                <StatCard
                  icon={Star}
                  label="Average Score"
                  value={`${qualityAssessmentData.data.aiAssessment.summary.averageScore}/16`}
                  subValue={`${((qualityAssessmentData.data.aiAssessment.summary.averageScore / 16) * 100).toFixed(0)}% of max`}
                  color="amber"
                />
                <StatCard
                  icon={CheckCircle}
                  label="Good Match Rate"
                  value={`${qualityAssessmentData.data.aiAssessment.summary.goodMatchRate}%`}
                  subValue={`${qualityAssessmentData.data.aiAssessment.summary.goodMatchCount} providers`}
                  color="green"
                />
              </div>

              {/* AI Model Performance Cards */}
              {qualityAssessmentData.data.aiAssessment.modelPerformance.length > 0 && (
                <div>
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Trophy className="h-5 w-5 text-purple-500" />
                    AI Assessment - Model Performance
                  </h2>
                  <div className="grid gap-4 lg:grid-cols-3">
                    {qualityAssessmentData.data.aiAssessment.modelPerformance.map((model, index) => (
                      <AIQualityModelCard
                        key={model.model}
                        model={model}
                        isLeader={index === 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* AI Score Distribution */}
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <BarChart3 className="h-5 w-5 text-purple-400" />
                    AI Score Distribution
                  </h2>
                  <PercentageToggle
                    showPercentages={showPercentages}
                    onToggle={() => setShowPercentages(!showPercentages)}
                  />
                </div>
                <div className="space-y-4">
                  {(() => {
                    const total = qualityAssessmentData.data.aiAssessment.summary.totalProvidersAssessed;
                    const dist = qualityAssessmentData.data.aiAssessment.scoreDistribution;
                    const minBarWidth = showPercentages ? '48px' : '32px';
                    const getDisplay = (count: number) => {
                      if (count === 0) return '';
                      return showPercentages ? `${Math.round((count / total) * 100)}%` : count;
                    };
                    return (
                      <>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Excellent</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-green-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.excellent / total) * 100 : 0}%`,
                                minWidth: dist.excellent > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.excellent)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">13-16</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Good</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.good / total) * 100 : 0}%`,
                                minWidth: dist.good > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.good)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">10-12</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Adequate</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-amber-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.adequate / total) * 100 : 0}%`,
                                minWidth: dist.adequate > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.adequate)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">7-9</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Poor</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-orange-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.poor / total) * 100 : 0}%`,
                                minWidth: dist.poor > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.poor)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">4-6</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-24 text-sm font-medium text-gray-700">Inadequate</span>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="h-full bg-red-500 flex items-center justify-center text-white text-sm font-medium"
                              style={{
                                width: `${total > 0 ? (dist.inadequate / total) * 100 : 0}%`,
                                minWidth: dist.inadequate > 0 ? minBarWidth : '0',
                              }}
                            >
                              {getDisplay(dist.inadequate)}
                            </div>
                          </div>
                          <span className="w-16 text-sm text-gray-500 text-right">0-3</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* AI Average Dimension Scores */}
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Target className="h-5 w-5 text-purple-400" />
                  AI Average Dimension Scores
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <DimensionScoreCard
                    label="Medical Fit"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.medicalFit}
                    max={3}
                    description="Condition-specialty alignment"
                  />
                  <DimensionScoreCard
                    label="Urgency/Timing"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.urgencyTiming}
                    max={3}
                    description="Timing and availability fit"
                  />
                  <DimensionScoreCard
                    label="Pathway Coherence"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.pathwayCoherence}
                    max={3}
                    description="Treatment approach coherence"
                  />
                  <DimensionScoreCard
                    label="Complexity/Risk"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.complexityRisk}
                    max={2}
                    description="Case complexity handling"
                  />
                  <DimensionScoreCard
                    label="Evidence Quality"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.evidenceQuality}
                    max={3}
                    description="Search result quality"
                  />
                  <DimensionScoreCard
                    label="Patient Centricity"
                    score={qualityAssessmentData.data.aiAssessment.averageDimensionScores.patientCentricity}
                    max={2}
                    description="Patient preferences fit"
                  />
                </div>
              </div>

              {/* Human vs AI Comparison Summary */}
              {hasPhase3Data && (
                <div className="rounded-xl border border-indigo-200 bg-white p-6">
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Activity className="h-5 w-5 text-indigo-600" />
                    Human vs AI Comparison
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Human Good Match Rate</div>
                      <div className="text-2xl font-bold text-green-600">
                        {qualityAssessmentData.data.summary.goodMatchRate}%
                      </div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">AI Good Match Rate</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {qualityAssessmentData.data.aiAssessment.summary.goodMatchRate}%
                      </div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Disagreements</div>
                      <div className="text-2xl font-bold text-amber-600">
                        {qualityAssessmentData.data.aiAssessment.summary.disagreementCount}
                      </div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Disagreement Rate</div>
                      <div className="text-2xl font-bold text-amber-600">
                        {qualityAssessmentData.data.aiAssessment.summary.disagreementRate}%
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabContent>
        )}

        {/* Stability Tab */}
        {hasStabilityData && stabilityData && (
          <TabContent value="stability">
            <div className="space-y-6">
              {/* Stability Summary */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={BarChart3}
                  label="Stability Test Runs"
                  value={stabilityData.data.summary.totalStabilityRuns}
                  subValue={`${stabilityData.data.summary.completedRuns} completed`}
                  color="purple"
                />
                <StatCard
                  icon={Layers}
                  label="Phase 1 Evaluations"
                  value={stabilityData.data.summary.phase1HumanEvaluations}
                  subValue={`${stabilityData.data.summary.phase1AIAnalyses} AI analyses`}
                  color="blue"
                />
                <StatCard
                  icon={Search}
                  label="Phase 2 Evaluations"
                  value={stabilityData.data.summary.phase2HumanEvaluations}
                  subValue={`${stabilityData.data.summary.phase2AIAnalyses} AI analyses`}
                  color="green"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Runs with Search Phase"
                  value={stabilityData.data.summary.runsWithPhase2}
                  subValue={`of ${stabilityData.data.summary.totalStabilityRuns} total`}
                  color="amber"
                />
              </div>

              {/* Model Performance Ranking */}
              {stabilityData.data.modelPerformance.length > 0 && (
                <div>
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Trophy className="h-5 w-5 text-purple-500" />
                    Model Stability Ranking
                  </h2>
                  <div className="grid gap-4 lg:grid-cols-3">
                    {stabilityData.data.modelPerformance.map((model, index) => (
                      <StabilityModelCard
                        key={model.model}
                        model={model}
                        isLeader={index === 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Phase 1: Case Analysis Stability */}
              <div className="rounded-xl border border-blue-200 bg-white p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Layers className="h-5 w-5 text-blue-600" />
                  Phase 2+3: Case Analysis Stability
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Human Evaluation */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Human Evaluation ({stabilityData.data.phase1.human.total} evaluations)
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(stabilityData.data.phase1.human.verdictDistribution).map(([verdict, count]) => (
                        <div key={verdict} className="flex items-center justify-between">
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            verdict === 'STABLE' ? 'bg-green-100 text-green-700' :
                            verdict === 'MOSTLY_STABLE' ? 'bg-blue-100 text-blue-700' :
                            verdict === 'SOMEWHAT_UNSTABLE' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {verdict.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                      {Object.keys(stabilityData.data.phase1.human.verdictDistribution).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No human evaluations yet</p>
                      )}
                    </div>
                  </div>
                  {/* AI Analysis */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      AI Consistency Analysis ({stabilityData.data.phase1.ai.total} analyses)
                    </h3>
                    {stabilityData.data.phase1.ai.averageScore !== null && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600 mb-1">Average Consistency Score</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {stabilityData.data.phase1.ai.averageScore}%
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {Object.entries(stabilityData.data.phase1.ai.assessmentDistribution).map(([assessment, count]) => (
                        <div key={assessment} className="flex items-center justify-between">
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            assessment.includes('HIGH') || assessment.includes('EXCELLENT') ? 'bg-green-100 text-green-700' :
                            assessment.includes('MEDIUM') || assessment.includes('MODERATE') ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {assessment.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                      {Object.keys(stabilityData.data.phase1.ai.assessmentDistribution).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No AI analyses yet</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* Agreement Rate */}
                {stabilityData.data.phase1.agreement.comparisons > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Human-AI Agreement Rate</span>
                      <span className={clsx(
                        'text-lg font-bold',
                        (stabilityData.data.phase1.agreement.agreementRate ?? 0) >= 80 ? 'text-green-600' :
                        (stabilityData.data.phase1.agreement.agreementRate ?? 0) >= 60 ? 'text-amber-600' :
                        'text-red-600'
                      )}>
                        {stabilityData.data.phase1.agreement.agreementRate ?? 'N/A'}%
                        <span className="text-xs font-normal text-gray-500 ml-1">
                          ({stabilityData.data.phase1.agreement.agreements}/{stabilityData.data.phase1.agreement.comparisons})
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Phase 2: Care Provider Matching Stability */}
              <div className="rounded-xl border border-green-200 bg-white p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Search className="h-5 w-5 text-green-600" />
                  Phase 4: Care Provider Matching Stability
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Human Evaluation */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Human Evaluation ({stabilityData.data.phase2.human.total} evaluations)
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(stabilityData.data.phase2.human.verdictDistribution).map(([verdict, count]) => (
                        <div key={verdict} className="flex items-center justify-between">
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            verdict === 'STABLE' ? 'bg-green-100 text-green-700' :
                            verdict === 'MOSTLY_STABLE' ? 'bg-blue-100 text-blue-700' :
                            verdict === 'SOMEWHAT_UNSTABLE' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {verdict.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                      {Object.keys(stabilityData.data.phase2.human.verdictDistribution).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No human evaluations yet</p>
                      )}
                    </div>
                  </div>
                  {/* AI Analysis */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      AI Consistency Analysis ({stabilityData.data.phase2.ai.total} analyses)
                    </h3>
                    {stabilityData.data.phase2.ai.averageScore !== null && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600 mb-1">Average Consistency Score</div>
                        <div className="text-2xl font-bold text-green-600">
                          {stabilityData.data.phase2.ai.averageScore}%
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {Object.entries(stabilityData.data.phase2.ai.assessmentDistribution).map(([assessment, count]) => (
                        <div key={assessment} className="flex items-center justify-between">
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            assessment.includes('HIGH') || assessment.includes('EXCELLENT') ? 'bg-green-100 text-green-700' :
                            assessment.includes('MEDIUM') || assessment.includes('MODERATE') ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            {assessment.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                        </div>
                      ))}
                      {Object.keys(stabilityData.data.phase2.ai.assessmentDistribution).length === 0 && (
                        <p className="text-sm text-gray-500 italic">No AI analyses yet</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* Agreement Rate */}
                {stabilityData.data.phase2.agreement.comparisons > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Human-AI Agreement Rate</span>
                      <span className={clsx(
                        'text-lg font-bold',
                        (stabilityData.data.phase2.agreement.agreementRate ?? 0) >= 80 ? 'text-green-600' :
                        (stabilityData.data.phase2.agreement.agreementRate ?? 0) >= 60 ? 'text-amber-600' :
                        'text-red-600'
                      )}>
                        {stabilityData.data.phase2.agreement.agreementRate ?? 'N/A'}%
                        <span className="text-xs font-normal text-gray-500 ml-1">
                          ({stabilityData.data.phase2.agreement.agreements}/{stabilityData.data.phase2.agreement.comparisons})
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Test Run Details Table */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">Stability Test Runs</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Test Run</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Category</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">Iterations</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">P1 Human</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">P1 AI</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">P2 Human</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">P2 AI</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {stabilityData.data.byTestRun.slice(0, 10).map((run) => (
                        <tr key={run.testRunId} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{run.testRunName}</div>
                            <div className="text-xs text-gray-500">{run.testCaseName} ({run.testCaseCode})</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                              {run.category.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-medium text-gray-900">
                              {run.completedIterations}/{run.totalIterations}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {run.phase1.humanVerdict ? (
                              <span className={clsx(
                                'px-2 py-1 rounded text-xs font-medium',
                                run.phase1.humanVerdict === 'STABLE' ? 'bg-green-100 text-green-700' :
                                run.phase1.humanVerdict === 'MOSTLY_STABLE' ? 'bg-blue-100 text-blue-700' :
                                run.phase1.humanVerdict === 'SOMEWHAT_UNSTABLE' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              )}>
                                {run.phase1.humanVerdict.replace(/_/g, ' ')}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {run.phase1.aiScore !== null ? (
                              <span className={clsx(
                                'px-2 py-1 rounded text-xs font-medium',
                                run.phase1.aiScore >= 80 ? 'bg-green-100 text-green-700' :
                                run.phase1.aiScore >= 60 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              )}>
                                {run.phase1.aiScore}%
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {run.phase2.humanVerdict ? (
                              <span className={clsx(
                                'px-2 py-1 rounded text-xs font-medium',
                                run.phase2.humanVerdict === 'STABLE' ? 'bg-green-100 text-green-700' :
                                run.phase2.humanVerdict === 'MOSTLY_STABLE' ? 'bg-blue-100 text-blue-700' :
                                run.phase2.humanVerdict === 'SOMEWHAT_UNSTABLE' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              )}>
                                {run.phase2.humanVerdict.replace(/_/g, ' ')}
                              </span>
                            ) : run.phase2.hasSearchPhase ? (
                              <span className="text-xs text-gray-400">Pending</span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {run.phase2.aiScore !== null ? (
                              <span className={clsx(
                                'px-2 py-1 rounded text-xs font-medium',
                                run.phase2.aiScore >= 80 ? 'bg-green-100 text-green-700' :
                                run.phase2.aiScore >= 60 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              )}>
                                {run.phase2.aiScore}%
                              </span>
                            ) : run.phase2.hasSearchPhase ? (
                              <span className="text-xs text-gray-400">Pending</span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {stabilityData.data.byTestRun.length > 10 && (
                  <div className="p-3 bg-gray-50 text-center text-sm text-gray-500">
                    Showing 10 of {stabilityData.data.byTestRun.length} test runs
                  </div>
                )}
              </div>
            </div>
          </TabContent>
        )}
      </Tabs>
    </div>
  );
}

// Dimension Score Card Component
function DimensionScoreCard({
  label,
  score,
  max,
  description,
}: {
  label: string;
  score: number;
  max: number;
  description: string;
}) {
  const percentage = (score / max) * 100;
  const scoreColor =
    percentage >= 80
      ? 'text-green-600'
      : percentage >= 60
      ? 'text-blue-600'
      : percentage >= 40
      ? 'text-amber-600'
      : 'text-red-600';

  return (
    <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <div className={clsx('text-lg font-bold', scoreColor)}>{score.toFixed(1)}</div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              percentage >= 80
                ? 'bg-green-500'
                : percentage >= 60
                ? 'bg-blue-500'
                : percentage >= 40
                ? 'bg-amber-500'
                : 'bg-red-500'
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-500">/ {max}</span>
      </div>
    </div>
  );
}

// Quality Model Card Component
function QualityModelCard({
  model,
  isLeader,
}: {
  model: QualityAssessmentResponse['data']['modelPerformance'][0];
  isLeader?: boolean;
}) {
  const config = MODEL_CONFIG[model.model] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  };

  const qualityPercentage = (model.averageQualityScore / 16) * 100;

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-green-400 ring-2 ring-green-100' : 'border-gray-200',
      )}
    >
      {/* Header */}
      <div className={clsx('px-5 py-4 flex items-center justify-between', config.bgColor)}>
        <div className="flex items-center gap-3">
          <ModelLogo model={model.model} size="md" />
          <div>
            <h3 className={clsx('font-bold', config.textColor)}>{getModelName(model.model)}</h3>
            <p className="text-xs text-gray-500">{model.totalAssessments} providers assessed</p>
          </div>
        </div>
        {isLeader && (
          <div className="flex items-center gap-1 rounded-full bg-green-200 px-2.5 py-1">
            <Crown className="h-3.5 w-3.5 text-green-700" />
            <span className="text-xs font-semibold text-green-700">Top Quality</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Average Quality Score</span>
          <div className="flex items-center gap-2">
            <span
              className={clsx(
                'text-2xl font-bold',
                qualityPercentage >= 80
                  ? 'text-green-600'
                  : qualityPercentage >= 60
                  ? 'text-blue-600'
                  : qualityPercentage >= 40
                  ? 'text-amber-600'
                  : 'text-red-600'
              )}
            >
              {model.averageQualityScore}
            </span>
            <span className="text-sm text-gray-400">/ 16</span>
          </div>
        </div>

        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              qualityPercentage >= 80
                ? 'bg-green-500'
                : qualityPercentage >= 60
                ? 'bg-blue-500'
                : qualityPercentage >= 40
                ? 'bg-amber-500'
                : 'bg-red-500'
            )}
            style={{ width: `${qualityPercentage}%` }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <span className="text-xs text-gray-500">Good Match Rate</span>
            <p
              className={clsx(
                'text-lg font-bold',
                model.goodMatchRate >= 80
                  ? 'text-green-600'
                  : model.goodMatchRate >= 60
                  ? 'text-blue-600'
                  : 'text-amber-600'
              )}
            >
              {model.goodMatchRate}%
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Good Matches</span>
            <p className="text-lg font-bold text-gray-900">{model.goodMatchCount}</p>
          </div>
        </div>

        {/* Dimension Scores Mini */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-600 mb-2">Dimension Averages</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Med Fit</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.medicalFit}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Urgency</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.urgencyTiming}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Therapy</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.therapyPathway}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Complex</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.complexityRisk}/2</p>
            </div>
            <div>
              <span className="text-gray-500">Outcome</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.outcomeFit}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Patient</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.patientCentricity}/2</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// AI Quality Model Card Component
function AIQualityModelCard({
  model,
  isLeader,
}: {
  model: NonNullable<QualityAssessmentResponse['data']['aiAssessment']>['modelPerformance'][0];
  isLeader?: boolean;
}) {
  const config = MODEL_CONFIG[model.model] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  };

  const qualityPercentage = (model.averageQualityScore / 16) * 100;

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200',
      )}
    >
      {/* Header */}
      <div className={clsx('px-5 py-4 flex items-center justify-between', config.bgColor)}>
        <div className="flex items-center gap-3">
          <ModelLogo model={model.model} size="md" />
          <div>
            <h3 className={clsx('font-bold', config.textColor)}>{getModelName(model.model)}</h3>
            <p className="text-xs text-gray-500">{model.totalAssessments} providers assessed</p>
          </div>
        </div>
        {isLeader && (
          <div className="flex items-center gap-1 rounded-full bg-purple-200 px-2.5 py-1">
            <Crown className="h-3.5 w-3.5 text-purple-700" />
            <span className="text-xs font-semibold text-purple-700">Top AI Quality</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Average Quality Score</span>
          <div className="flex items-center gap-2">
            <span
              className={clsx(
                'text-2xl font-bold',
                qualityPercentage >= 80
                  ? 'text-green-600'
                  : qualityPercentage >= 60
                  ? 'text-blue-600'
                  : qualityPercentage >= 40
                  ? 'text-amber-600'
                  : 'text-red-600'
              )}
            >
              {model.averageQualityScore}
            </span>
            <span className="text-sm text-gray-400">/ 16</span>
          </div>
        </div>

        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              qualityPercentage >= 80
                ? 'bg-green-500'
                : qualityPercentage >= 60
                ? 'bg-blue-500'
                : qualityPercentage >= 40
                ? 'bg-amber-500'
                : 'bg-red-500'
            )}
            style={{ width: `${qualityPercentage}%` }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <span className="text-xs text-gray-500">Good Match Rate</span>
            <p
              className={clsx(
                'text-lg font-bold',
                model.goodMatchRate >= 80
                  ? 'text-green-600'
                  : model.goodMatchRate >= 60
                  ? 'text-blue-600'
                  : 'text-amber-600'
              )}
            >
              {model.goodMatchRate}%
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Good Matches</span>
            <p className="text-lg font-bold text-gray-900">{model.goodMatchCount}</p>
          </div>
        </div>

        {/* Dimension Scores Mini */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-600 mb-2">Dimension Averages</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Med Fit</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.medicalFit}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Urgency</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.urgencyTiming}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Pathway</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.pathwayCoherence}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Complex</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.complexityRisk}/2</p>
            </div>
            <div>
              <span className="text-gray-500">Evidence</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.evidenceQuality}/3</p>
            </div>
            <div>
              <span className="text-gray-500">Patient</span>
              <p className="font-medium text-gray-900">{model.averageDimensionScores.patientCentricity}/2</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Stability Model Card Component
function StabilityModelCard({
  model,
  isLeader,
}: {
  model: StabilityAnalyticsResponse['data']['modelPerformance'][0];
  isLeader?: boolean;
}) {
  const config = MODEL_CONFIG[model.model] || {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  };

  const phase1Score = model.phase1.averageConsistencyScore ?? 0;
  const phase2Score = model.phase2.averageConsistencyScore ?? 0;

  // Calculate stability rate from verdicts
  const phase1StableCount = (model.phase1.verdictDistribution['STABLE'] || 0) +
    (model.phase1.verdictDistribution['MOSTLY_STABLE'] || 0);
  const phase1TotalVerdicts = model.phase1.humanEvaluations;
  const phase1StabilityRate = phase1TotalVerdicts > 0
    ? Math.round((phase1StableCount / phase1TotalVerdicts) * 100)
    : null;

  return (
    <div
      className={clsx(
        'rounded-xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg',
        isLeader ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200',
      )}
    >
      {/* Header */}
      <div className={clsx('px-5 py-4 flex items-center justify-between', config.bgColor)}>
        <div className="flex items-center gap-3">
          <ModelLogo model={model.model} size="md" />
          <div>
            <h3 className={clsx('font-bold', config.textColor)}>{getModelName(model.model)}</h3>
            <p className="text-xs text-gray-500">{model.totalRuns} stability runs</p>
          </div>
        </div>
        {isLeader && (
          <div className="flex items-center gap-1 rounded-full bg-purple-200 px-2.5 py-1">
            <Crown className="h-3.5 w-3.5 text-purple-700" />
            <span className="text-xs font-semibold text-purple-700">Most Stable</span>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="p-5 space-y-4">
        {/* Phase 1 Consistency */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Phase 2+3 AI Consistency</span>
            <span
              className={clsx(
                'text-xl font-bold',
                phase1Score >= 80 ? 'text-green-600' :
                phase1Score >= 60 ? 'text-blue-600' :
                phase1Score >= 40 ? 'text-amber-600' :
                'text-red-600'
              )}
            >
              {model.phase1.averageConsistencyScore !== null ? `${phase1Score}%` : 'N/A'}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                phase1Score >= 80 ? 'bg-green-500' :
                phase1Score >= 60 ? 'bg-blue-500' :
                phase1Score >= 40 ? 'bg-amber-500' :
                'bg-red-500'
              )}
              style={{ width: `${phase1Score}%` }}
            />
          </div>
        </div>

        {/* Phase 2 Consistency (always show for visual consistency) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Phase 4 AI Consistency</span>
            <span
              className={clsx(
                'text-xl font-bold',
                model.phase2.averageConsistencyScore === null ? 'text-gray-400' :
                phase2Score >= 80 ? 'text-green-600' :
                phase2Score >= 60 ? 'text-blue-600' :
                phase2Score >= 40 ? 'text-amber-600' :
                'text-red-600'
              )}
            >
              {model.phase2.averageConsistencyScore !== null ? `${phase2Score}%` : 'N/A'}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                model.phase2.averageConsistencyScore === null ? 'bg-gray-200' :
                phase2Score >= 80 ? 'bg-green-500' :
                phase2Score >= 60 ? 'bg-blue-500' :
                phase2Score >= 40 ? 'bg-amber-500' :
                'bg-red-500'
              )}
              style={{ width: model.phase2.averageConsistencyScore !== null ? `${phase2Score}%` : '0%' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">Human Stability Rate</span>
            <p
              className={clsx(
                'text-lg font-bold',
                (phase1StabilityRate ?? 0) >= 80 ? 'text-green-600' :
                (phase1StabilityRate ?? 0) >= 60 ? 'text-blue-600' :
                'text-amber-600'
              )}
            >
              {phase1StabilityRate !== null ? `${phase1StabilityRate}%` : 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Completed Runs</span>
            <p className="text-lg font-bold text-gray-900">
              {model.completedRuns}/{model.totalRuns}
            </p>
          </div>
        </div>

        {/* Verdict Distribution (always show for visual consistency) */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-600 mb-2">Human Verdict Distribution</p>
          {model.phase1.humanEvaluations > 0 ? (
            <div className="flex gap-1">
              {Object.entries(model.phase1.verdictDistribution).map(([verdict, count]) => (
                <div
                  key={verdict}
                  className={clsx(
                    'flex-1 text-center py-1 rounded text-xs font-medium',
                    verdict === 'STABLE' ? 'bg-green-100 text-green-700' :
                    verdict === 'MOSTLY_STABLE' ? 'bg-blue-100 text-blue-700' :
                    verdict === 'SOMEWHAT_UNSTABLE' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  )}
                  title={verdict.replace(/_/g, ' ')}
                >
                  {count}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No evaluations yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
